/**
 * PendingCashoutsPanel — admin-style cashout approval queue. Matches the
 * row layout of the main admin Cashouts tab so operators and cashiers get
 * the same workflow:
 *   - Player name + phone + current balance
 *   - Method + handle
 *   - Requested amount, age
 *   - Quick play-it-back presets (refund partial)
 *   - Approve & Reject buttons
 *
 * Backend endpoints:
 *   GET  /api/operator/cashout-requests   (operator role)
 *   GET  /api/cashier/cashout-requests    (cashier role)
 *   POST /api/cashier/cashout-action      (operator + cashier — backend
 *                                          enforces visibility + can_cashout)
 *   POST /api/cashier/cashout-playback    (partial refund / "play it back")
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
    Inbox, CheckCircle2, XCircle, Loader2, Undo2, Phone, User, RefreshCw, Bell, BellOff,
} from 'lucide-react';
import { setTabSignal, playChime } from '../lib/tabAlerts';
import { PlayerName } from './PlayerDrilldownContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const QUICK_PLAY_BACK = [5, 10, 20, 50];
const MUTE_KEY = 'pendingCashouts.muted';

function ageMs(iso) {
    if (!iso) return 0;
    try { return Date.now() - new Date(iso).getTime(); } catch { return 0; }
}
function fmtAge(iso) {
    const ms = ageMs(iso);
    if (ms < 60_000) return 'just now';
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
}

const PendingCashoutsPanel = ({ role = 'operator' }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [rejectFor, setRejectFor] = useState(null);
    const [rejectNote, setRejectNote] = useState('');
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
    });
    // Track IDs we've already seen so we only chime on truly new requests.
    const seenIdsRef = useRef(new Set());
    const isFirstLoadRef = useRef(true);
    const mutedRef = useRef(muted);
    useEffect(() => { mutedRef.current = muted; }, [muted]);

    const toggleMute = () => {
        setMuted((m) => {
            const next = !m;
            try { localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    };

    const endpoint = role === 'operator' ? '/operator/cashout-requests' : '/cashier/cashout-requests';
    const signalKey = `pending-cashouts-${role}`;

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}${endpoint}`, { withCredentials: true });
            const list = data || [];
            setItems(list);

            // Detect new arrivals (skip chime/toast on the very first load to
            // avoid an alarm every time the page is opened).
            const ids = new Set(list.map((r) => r.id));
            if (!isFirstLoadRef.current) {
                const newOnes = list.filter((r) => !seenIdsRef.current.has(r.id));
                if (newOnes.length > 0 && !mutedRef.current) {
                    try { playChime(); } catch { /* noop */ }
                    const first = newOnes[0];
                    toast.message(
                        newOnes.length === 1
                            ? `New cashout request · ${first.user_name} · ${fmt(Math.abs(first.amount || 0))}`
                            : `${newOnes.length} new cashout requests`,
                        { duration: 4500 }
                    );
                }
            }
            seenIdsRef.current = ids;
            isFirstLoadRef.current = false;

            // Reflect count + favicon dot in the browser tab.
            setTabSignal(signalKey, list.length > 0 ? 'info' : null, list.length);
        } catch (err) {
            // Silently swallow on background polls so a transient blip doesn't toast
            // every 15 seconds. The initial load below handles the "first time"
            // failure case loudly.
        } finally { setLoading(false); }
    }, [endpoint, signalKey]);

    // Initial load + background polling so cashiers see new pending cashouts
    // and play-back events as they happen.
    useEffect(() => {
        load();
        const id = setInterval(() => { load(); }, 15_000);
        return () => {
            clearInterval(id);
            // Clear our tab signal when the panel unmounts so the favicon/title
            // don't get stuck showing a stale counter.
            setTabSignal(signalKey, null, 0);
        };
    }, [load, signalKey]);

    const approve = async (req) => {
        setBusyId(`${req.id}-approve`);
        try {
            // PHASE 3 · Atomic first-claim. Two cashiers who tap Approve at the
            // same instant → backend gives exactly one the row; the other gets
            // 409 with the winner's name so we can render a clean rejection.
            try {
                await axios.post(`${API}/cashier/payouts/${req.id}/claim`,
                    null, { withCredentials: true, params: { hold_seconds: 60 } });
            } catch (claimErr) {
                const d = claimErr?.response?.data?.detail;
                if (claimErr?.response?.status === 409) {
                    if (d?.error === 'CLAIMED_BY_ANOTHER_CASHIER') {
                        toast.error(`Locked by ${d.claimed_by_name || 'another cashier'}`, {
                            description: `They claimed this ${d.claimed_at ? 'just now' : 'first'}. Refresh to re-check.`,
                        });
                    } else if (d?.error === 'ALREADY_RESOLVED') {
                        toast.info(`Already ${d.status}`);
                    } else {
                        toast.error('Could not claim payout');
                    }
                    await load();
                    return;
                }
                throw claimErr;
            }
            await axios.post(`${API}/cashier/cashout-action`,
                { transaction_id: req.id, action: 'approve' },
                { withCredentials: true });
            toast.success(`Approved ${fmt(Math.abs(req.amount))} for ${req.user_name}`);
            await load();
        } catch (err) {
            // If the follow-up approve failed, release our claim so another
            // cashier can try.
            try {
                await axios.post(`${API}/cashier/payouts/${req.id}/release`, null,
                    { withCredentials: true });
            } catch { /* best-effort */ }
            toast.error(err.response?.data?.detail || 'Approve failed');
        } finally { setBusyId(null); }
    };

    const playBack = async (req, amount) => {
        setBusyId(`${req.id}-pb-${amount ?? 'all'}`);
        try {
            const { data } = await axios.post(`${API}/cashier/cashout-playback`,
                { transaction_id: req.id, amount: amount ?? null },
                { withCredentials: true });
            toast.success((amount
                ? `${fmt(amount)} played back to ${req.user_name}`
                : `All ${fmt(Math.abs(req.amount))} played back to ${req.user_name}`) +
                (data?.bonus_granted ? ` · +${fmt(data.bonus_granted)} load bonus` : ''));
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Play-back failed');
        } finally { setBusyId(null); }
    };

    const reject = async () => {
        if (!rejectFor) return;
        setBusyId(`${rejectFor.id}-reject`);
        try {
            await axios.post(`${API}/cashier/cashout-action`,
                { transaction_id: rejectFor.id, action: 'reject', note: rejectNote.trim() || undefined },
                { withCredentials: true });
            toast.success(`Rejected ${rejectFor.user_name}'s request`);
            setRejectFor(null);
            setRejectNote('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Reject failed');
        } finally { setBusyId(null); }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30" data-testid="pending-cashouts-panel">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                    <Inbox className="w-4 h-4 text-zinc-400" />
                    <h2 className="text-sm font-semibold text-zinc-100">Pending cashouts</h2>
                    {items.length > 0 && (
                        <span className="text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">
                            {items.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <Button onClick={load} size="sm" variant="outline"
                        className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="cashouts-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button onClick={toggleMute} size="sm" variant="outline"
                        className={`h-8 border-zinc-800 bg-zinc-900 hover:bg-zinc-800 ${muted ? 'text-zinc-500' : 'text-amber-300'}`}
                        title={muted ? 'Sound alerts muted — click to enable' : 'Sound alerts on — click to mute'}
                        data-testid="cashouts-mute-toggle">
                        {muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-8 text-zinc-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
                </div>
            ) : items.length === 0 ? (
                <p className="text-center text-zinc-500 py-8 text-sm">No pending cashout requests.</p>
            ) : (
                <div className="divide-y divide-zinc-800">
                    {items.map((r, i) => {
                        const amt = Math.abs(Number(r.amount) || 0);
                        const bal = Number(r.user_balance) || 0;
                        return (
                            <div key={r.id} className="p-4 space-y-3" data-testid={`cashout-row-${i}`}>
                                {/* Header row */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-medium text-zinc-100 flex items-center gap-2 truncate">
                                            <User className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                            <PlayerName id={r.user_id} className="text-zinc-100 font-medium">
                                                {r.user_name}
                                            </PlayerName>
                                            {r.owner_cashier_name && (
                                                <span className="text-[10px] text-zinc-500">· {r.owner_cashier_name}</span>
                                            )}
                                        </p>
                                        <p className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                                            {r.user_phone && (
                                                <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.user_phone}</span>
                                            )}
                                            <span>· via <span className="text-zinc-300">{r.payout_method || 'cash'}</span></span>
                                            {r.payout_handle && <span className="text-zinc-500">({r.payout_handle})</span>}
                                        </p>
                                        <p className="text-[10px] text-zinc-600 mt-0.5">
                                            {fmtAge(r.created_at)} ago · {new Date(r.created_at).toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="font-mono font-semibold text-amber-300 text-lg" data-testid={`cashout-row-amount-${i}`}>{fmt(amt)}</p>
                                        {r.net_amount != null && Math.abs(Number(r.net_amount) - amt) > 0.005 && (
                                            <p className="text-[10px] text-[#39FF14] font-mono" data-testid={`cashout-row-net-${i}`}>
                                                player receives {fmt(Number(r.net_amount))} · fee {fmt(Number(r.fee_amount || 0))}
                                            </p>
                                        )}
                                        <p className="text-[10px] text-zinc-500">Balance after: {fmt(bal)}</p>
                                    </div>
                                </div>

                                {/* Action row */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium pr-1">Play it back:</span>
                                    {QUICK_PLAY_BACK.filter(v => v <= amt).map(v => (
                                        <Button key={v} size="sm" variant="outline"
                                            disabled={busyId === `${r.id}-pb-${v}`}
                                            onClick={() => playBack(r, v)}
                                            className="h-7 px-2.5 border-zinc-800 bg-zinc-950 text-zinc-300 hover:bg-zinc-800 text-xs"
                                            data-testid={`cashout-pb-${i}-${v}`}>
                                            <Undo2 className="w-3 h-3 mr-1" />${v}
                                        </Button>
                                    ))}
                                    <Button size="sm" variant="outline"
                                        disabled={busyId === `${r.id}-pb-all`}
                                        onClick={() => playBack(r, null)}
                                        className="h-7 px-2.5 border-zinc-800 bg-zinc-950 text-zinc-300 hover:bg-zinc-800 text-xs"
                                        data-testid={`cashout-pb-${i}-all`}>
                                        <Undo2 className="w-3 h-3 mr-1" /> All
                                    </Button>

                                    {r.claimed_by_id && !r.claim_is_you && (
                                        <span
                                            data-testid={`cashout-locked-${i}`}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-amber-950/60 text-amber-300 border border-amber-800/60">
                                            🔒 Held by {r.claimed_by_name}
                                        </span>
                                    )}
                                    {r.claim_is_you && (
                                        <span
                                            data-testid={`cashout-locked-yours-${i}`}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-emerald-950/60 text-emerald-300 border border-emerald-800/60">
                                            🔒 Locked to you
                                        </span>
                                    )}

                                    <div className="flex-1" />

                                    <Button size="sm" variant="outline"
                                        disabled={busyId === `${r.id}-reject`}
                                        onClick={() => setRejectFor(r)}
                                        className="h-8 border-zinc-800 bg-zinc-900 text-rose-300 hover:bg-zinc-800"
                                        data-testid={`cashout-reject-${i}`}>
                                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                    </Button>
                                    <Button size="sm"
                                        disabled={busyId === `${r.id}-approve` || (r.claimed_by_id && !r.claim_is_you)}
                                        onClick={() => approve(r)}
                                        className="h-8 bg-zinc-100 text-zinc-900 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid={`cashout-approve-${i}`}>
                                        {busyId === `${r.id}-approve`
                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Reject dialog */}
            <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <DialogHeader>
                        <DialogTitle>Reject cashout</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {rejectFor?.user_name} · {fmt(Math.abs(rejectFor?.amount || 0))}. Credits will be refunded to the player.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="Reason (optional)"
                            className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="cashout-reject-note" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectFor(null)}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={reject} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="cashout-reject-confirm">
                            Reject & refund
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PendingCashoutsPanel;
